'use client';

import React, { useEffect, useRef, useState } from 'react';
import { ParticleSettings } from '@/lib/widgetSettings';
import { getCurrentVfxTier, type VfxTier } from '@/utils/vfxManager';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  opacity: number;
  life: number;
  maxLife: number;
}

interface WidgetParticlesProps {
  settings: ParticleSettings;
  width: number;
  height: number;
  className?: string;
}

export default function WidgetParticles({ settings, width, height, className = '' }: WidgetParticlesProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | null>(null);
  const particlesRef = useRef<Particle[]>([]);
  const [isVisible, setIsVisible] = useState(false);
  const [vfxTier, setVfxTier] = useState<VfxTier>('max');

  // Комментарий на русском: отслеживаем VFX tier.
  useEffect(() => {
    const updateTier = () => {
      const tier = getCurrentVfxTier();
      if (tier) setVfxTier(tier);
    };
    updateTier();

    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.attributeName === 'data-vfx') updateTier();
      }
    });
    observer.observe(document.documentElement, { attributes: true });
    return () => observer.disconnect();
  }, []);

  // Комментарий на русском: адаптация для режима medium — уменьшаем количество и скорость.
  const vfxMultiplier = vfxTier === 'medium' ? 0.5 : 1;
  const adaptedCount = Math.floor(settings.count * vfxMultiplier);
  const adaptedSpeed = settings.speed * vfxMultiplier;

  // Инициализация частиц
  const initParticles = () => {
    const particles: Particle[] = [];
    for (let i = 0; i < adaptedCount; i++) {
      particles.push({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * adaptedSpeed,
        vy: (Math.random() - 0.5) * adaptedSpeed,
        size: settings.size + Math.random() * 2,
        opacity: Math.random() * 0.5 + 0.3,
        life: 0,
        maxLife: 100 + Math.random() * 200
      });
    }
    particlesRef.current = particles;
  };

  // Анимация частиц
  const animate = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Очищаем canvas
    ctx.clearRect(0, 0, width, height);

    if (!settings.enabled) return;

    // Обновляем и рисуем частицы
    particlesRef.current.forEach((particle, index) => {
      // Обновляем позицию
      particle.x += particle.vx;
      particle.y += particle.vy;
      particle.life++;

      // Проверяем границы
      if (particle.x < 0 || particle.x > width || particle.y < 0 || particle.y > height) {
        // Пересоздаем частицу
        particle.x = Math.random() * width;
        particle.y = Math.random() * height;
        particle.life = 0;
      }

      // Рисуем частицу
      ctx.save();
      ctx.globalAlpha = particle.opacity * (1 - particle.life / particle.maxLife);
      ctx.fillStyle = settings.color;
      ctx.beginPath();
      ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    });

    animationRef.current = requestAnimationFrame(animate);
  };

  useEffect(() => {
    if (width > 0 && height > 0) {
      initParticles();
      setIsVisible(true);
      animate();
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [width, height, settings]);

  // Комментарий на русском: если VFX выключены — не рендерим частицы.
  if (!isVisible || !settings.enabled || vfxTier === 'off') {
    return null;
  }

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      className={`absolute inset-0 pointer-events-none ${className}`}
      style={{
        zIndex: 1,
        mixBlendMode: 'multiply'
      }}
    />
  );
}
